import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { PublicUser, CreateUserInput, AuthTokens } from '@flymind/shared';
import { api, setTokens, clearTokens, getAccessToken } from '../lib/api-client';

interface AuthContextValue {
  user: PublicUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: CreateUserInput) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const userData = await api.get<PublicUser>('/users/me');
      setUser(userData);
    } catch {
      setUser(null);
      clearTokens();
    }
  }, []);

  // Auto-check auth on mount
  useEffect(() => {
    const init = async () => {
      const token = getAccessToken();
      if (token) {
        await refreshUser();
      }
      setIsLoading(false);
    };
    init();
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await api.post<{ user: PublicUser; tokens: AuthTokens }>(
      '/auth/login',
      { email, password },
    );
    setTokens(result.tokens.accessToken, result.tokens.refreshToken);
    // Fetch full user profile (login response may not include role)
    const fullUser = await api.get<PublicUser>('/users/me');
    setUser(fullUser);
  }, []);

  const register = useCallback(async (data: CreateUserInput) => {
    const result = await api.post<{ user: PublicUser; tokens: AuthTokens }>(
      '/auth/register',
      data,
    );
    setTokens(result.tokens.accessToken, result.tokens.refreshToken);
    const fullUser = await api.get<PublicUser>('/users/me');
    setUser(fullUser);
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      login,
      register,
      logout,
      refreshUser,
    }),
    [user, isLoading, login, register, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
